import React from 'react';

import { Text } from '@/nova/components';
import { FINISH } from '../../finish.tokens';
import {
  Root,
  IconCircle,
  Heading,
  Body,
  RetryButton,
  RetryLabel,
  SupportLine,
  SupportLink,
} from './CheckoutError.styles';

export type CheckoutErrorProps = {
  /**
   * The session is gone (vs. a recoverable network/Stripe failure). When set,
   * the copy explains the session lapsed and the retry button is hidden — a
   * refetch can't bring a missing session back.
   */
  noSession?: boolean;
  /** Re-attempt starting checkout. Shown as the "Try again" button when present. */
  onRetry?: () => void;
};

/** Warning glyph (rounded triangle + exclamation) — stroke from the error token. */
function WarningGlyph(): React.ReactElement {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3.6 21.2 19.5a1.4 1.4 0 0 1-1.2 2.1H4a1.4 1.4 0 0 1-1.2-2.1L12 3.6Z"
        stroke={FINISH.errorIconStroke}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M12 9.5v4.2"
        stroke={FINISH.errorIconStroke}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17.2" r="1.05" fill={FINISH.errorIconStroke} />
    </svg>
  );
}

/**
 * Friendly, centred error state for when the step-4 checkout can't START — the
 * invoice/Stripe fetch failed, or there's no active session. Replaces the old
 * bare red sentence with a glyph, a clear heading, a calm explanation, an
 * optional "Try again" button, and a support fallback.
 *
 * (A *declined* payment is handled inline by `PaymentChrome` instead, so the
 * buyer keeps the card details they already entered.)
 */
export const CheckoutError = ({ noSession, onRetry }: CheckoutErrorProps): React.ReactElement => (
  <Root>
    <IconCircle>
      <WarningGlyph />
    </IconCircle>

    {noSession ? (
      <>
        <Heading text="Your checkout session expired" tag="h2" />
        <Body
          text="This checkout is no longer active. Go back and start again to pick up where you left off."
          tag="paragraph"
        />
      </>
    ) : (
      <>
        <Heading text="We couldn’t start checkout" tag="h2" />
        <Body
          text="Something went wrong reaching our secure payment service. Check your connection and try again. You won’t be charged until you confirm."
          tag="paragraph"
        />
      </>
    )}

    {onRetry ? (
      <RetryButton type="button" data-ga-id="cfg-finish-checkout-error-retry" onClick={onRetry}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M20 11a8 8 0 1 0-.6 4"
            stroke={FINISH.payButtonText}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M20 4v5h-5"
            stroke={FINISH.payButtonText}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <RetryLabel text="Try again" tag="span" />
      </RetryButton>
    ) : null}

    <SupportLine>
      <Text text="Still having trouble? " tag="span" />
      <SupportLink text="support@whitelabels.com" tag="span" />
    </SupportLine>
  </Root>
);

export default CheckoutError;
